import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useOrders } from "@/lib/orders";
import { useWishlist } from "@/lib/wishlist/store";
import { useAddressBook } from "@/lib/account/addresses";
import { useProfile } from "@/lib/account/profile";
import { AccountShell, SectionCard } from "@/components/account/AccountShell";
import { formatPrice } from "@/lib/cart/pricing";

export const Route = createFileRoute("/account/")({
  component: DashboardPage,
});

function DashboardPage() {
  const { user, signOut } = useAuth();
  const { orders } = useOrders();
  const wishlist = useWishlist();
  const { addresses } = useAddressBook();
  const [profile] = useProfile();
  const displayName =
    [profile.firstName, profile.lastName].filter(Boolean).join(" ") || (user?.email ?? "Guest");

  const stats = [
    { label: "Orders", value: orders.length, to: "/account/orders" },
    { label: "Wishlist", value: wishlist.length, to: "/account/wishlist" },
    { label: "Addresses", value: addresses.length, to: "/account/addresses" },
  ];

  const recent = orders.slice(0, 3);

  return (
    <AccountShell>
      <SectionCard title="Overview">
        <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-black/50">
              Signed in as
            </p>
            <p
              className="mt-1 text-2xl text-black"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
            >
              {displayName}
            </p>
            {user?.email && <p className="mt-1 text-sm text-black/60">{user.email}</p>}
          </div>
          <button
            onClick={() => signOut()}
            className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/60 underline-offset-4 transition hover:text-[#C6A15B] hover:underline"
          >
            Sign Out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6">
          {stats.map((s) => (
            <Link
              key={s.label}
              to={s.to}
              className="border border-black/10 p-4 text-center transition hover:border-[#C6A15B] sm:p-6"
            >
              <p
                className="text-3xl text-black sm:text-4xl"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
              >
                {s.value}
              </p>
              <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.22em] text-black/60 sm:text-[11px]">
                {s.label}
              </p>
            </Link>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Recent Orders"
        action={
          <Link
            to="/account/orders"
            className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#C6A15B] hover:underline"
          >
            View all
          </Link>
        }
      >
        {recent.length === 0 ? (
          <p className="py-6 text-center text-sm text-black/60">
            You have no orders yet. Discover the collection to place your first.
          </p>
        ) : (
          <ul className="divide-y divide-black/10">
            {recent.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <Link
                    to="/order/$id"
                    params={{ id: o.id }}
                    className="text-sm font-medium text-black hover:text-[#C6A15B]"
                  >
                    {o.id}
                  </Link>
                  <p className="text-xs text-black/60">
                    {new Date(o.createdAt).toLocaleDateString()} · {o.lines.length} item
                    {o.lines.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <p className="text-sm text-black">{formatPrice(o.pricing.total)}</p>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </AccountShell>
  );
}
